import React, { useEffect, useMemo, useState } from "react";

// Lightweight local Card
function Card({ className = "", style = {}, children }) {
  const baseStyle = {
    border: "1px solid #ddd",
    borderRadius: 16,
    boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
    background: "#fff",
  };
  return (
    <div className={className} style={{ ...baseStyle, ...style }}>
      {children}
    </div>
  );
}

const nfmt = (v) => {
  if (v === null || v === undefined) return "-";
  const num = Number(v);
  if (Number.isNaN(num)) return v;
  return new Intl.NumberFormat().format(num);
};

const isoToLocal = (s) => {
  if (!s) return "-";
  try {
    return new Date(s).toLocaleString();
  } catch {
    return s;
  }
};

// Simple bar chart (no external chart libs)

// Simple SVG bar chart (no external chart libs)
function SvgBarChart({
  title,
  data,
  xKey,
  yKey,
  formatX = (v) => String(v ?? ""),
  formatY = (v) => `$${Number(v || 0).toFixed(0)}`,
  height = 220,
}) {
  if (!Array.isArray(data) || data.length === 0) return null;

  const values = data.map((d) => Number(d?.[yKey] ?? 0));
  const maxVal = Math.max(...values, 0);
  const minVal = Math.min(...values, 0);
  const range = maxVal - minVal || 1;

  // show fewer x labels if too many bars
  const n = data.length;
  const labelEvery = n > 20 ? 4 : n > 14 ? 2 : 1;

  const padding = { top: 22, right: 12, bottom: 34, left: 44 };
  const w = 900; // viewBox width (scales to container)
  const h = height;
  const innerW = w - padding.left - padding.right;
  const innerH = h - padding.top - padding.bottom;

  const step = innerW / n;
  const barW = Math.max(6, Math.min(40, step * 0.7));

  const yToPx = (v) => padding.top + ((maxVal - v) / range) * innerH;
  const y0 = yToPx(0);

  return (
    <div style={{ width: "100%" }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>

      <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 10 }}>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label={title}>
          {/* y-axis */}
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={h - padding.bottom} stroke="#666" />
          {/* x-axis at 0 */}
          <line x1={padding.left} y1={y0} x2={w - padding.right} y2={y0} stroke="#666" />

          {/* range labels */}
          <text x={padding.left + 4} y={padding.top - 6} fontSize="12" fill="#333">
            {`Max: ${formatY(maxVal)}`}
          </text>
          {minVal < 0 ? (
            <text x={padding.left + 4} y={padding.top + 10} fontSize="12" fill="#333">
              {`Min: ${formatY(minVal)}`}
            </text>
          ) : null}

          {/* bars */}
          {data.map((d, i) => {
            const raw = Number(d?.[yKey] ?? 0);
            const v = Number.isFinite(raw) ? raw : 0;

            const x = padding.left + i * step + (step - barW) / 2;

            const yV = yToPx(v);
            const bh = Math.abs(yV - y0);
            const y = v >= 0 ? yV : y0;

            const isNeg = v < 0;

            // value label position
            const labelY = isNeg ? Math.min(h - padding.bottom - 4, y + bh + 12) : Math.max(padding.top + 10, y - 6);

            return (
              <g key={String(d?.[xKey] ?? i)}>
                <rect x={x} y={y} width={barW} height={bh} fill={isNeg ? "#d32f2f" : "#1976d2"} />
                <text x={x + barW / 2} y={labelY} fontSize="11" fill="#111" textAnchor="middle">
                  {formatY(v)}
                </text>

                {i % labelEvery === 0 ? (
                  <text x={x + barW / 2} y={h - padding.bottom + 16} fontSize="11" fill="#333" textAnchor="middle">
                    {formatX(d?.[xKey])}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}

function POProfitTrendBarChart({ data, height = 220, titleSuffix = "latest 30" }) {
  // expected: [{ purchase_order_id, realized_profit, projected_remaining_profit, total_projected_profit }]
  if (!Array.isArray(data) || data.length === 0) return null;

  const values = data.map((d) => {
    const realized = Number(d?.realized_profit ?? 0) || 0;
    const remaining = Number(d?.projected_remaining_profit ?? 0) || 0;
    return realized + remaining;
  });

  const maxVal = Math.max(...values, 0);
  const minVal = Math.min(...values, 0);
  const range = maxVal - minVal || 1;

  const n = data.length;
  const labelEvery = n > 20 ? 4 : n > 14 ? 2 : 1;

  const padding = { top: 22, right: 12, bottom: 34, left: 44 };
  const w = 900;
  const h = height;
  const innerW = w - padding.left - padding.right;
  const innerH = h - padding.top - padding.bottom;

  const step = innerW / n;
  const barW = Math.max(6, Math.min(40, step * 0.7));

  const yToPx = (v) => padding.top + ((maxVal - v) / range) * innerH;
  const y0 = yToPx(0);

  const formatY = (v) => `$${Number(v || 0).toFixed(2)}`;

  return (
    <div style={{ width: "100%" }}>
      <div style={{ fontWeight: 600, marginBottom: 6 }}>
        PO profit (Realized + Remaining) — {titleSuffix}
        <span style={{ fontWeight: 400, marginLeft: 10, fontSize: 12, color: "#555" }}>
          <span style={{ display: "inline-block", width: 10, height: 10, background: "#1976d2", marginRight: 6, verticalAlign: "middle" }} />
          Realized
          <span style={{ display: "inline-block", width: 10, height: 10, background: "#7fb6e6", margin: "0 6px 0 12px", verticalAlign: "middle" }} />
          Remaining
        </span>
      </div>

      <div style={{ border: "1px solid #ddd", borderRadius: 12, padding: 10 }}>
        <svg viewBox={`0 0 ${w} ${h}`} width="100%" height={h} role="img" aria-label="PO profit trend">
          {/* y-axis */}
          <line x1={padding.left} y1={padding.top} x2={padding.left} y2={h - padding.bottom} stroke="#666" />
          {/* x-axis at 0 */}
          <line x1={padding.left} y1={y0} x2={w - padding.right} y2={y0} stroke="#666" />

          {/* range labels */}
          <text x={padding.left + 4} y={padding.top - 6} fontSize="12" fill="#333">
            {`Max: ${formatY(maxVal)}`}
          </text>
          {minVal < 0 ? (
            <text x={padding.left + 4} y={padding.top + 10} fontSize="12" fill="#333">
              {`Min: ${formatY(minVal)}`}
            </text>
          ) : null}

          {/* stacked bars */}
          {data.map((d, i) => {
            const poId = d?.purchase_order_id ?? i;

            const realizedRaw = Number(d?.realized_profit ?? 0);
            const remainingRaw = Number(d?.projected_remaining_profit ?? 0);

            const realized = Number.isFinite(realizedRaw) ? realizedRaw : 0;
            const remaining = Number.isFinite(remainingRaw) ? remainingRaw : 0;

            const total = realized + remaining;

            const x = padding.left + i * step + (step - barW) / 2;

            // Split into positive/negative lanes so the baseline remains meaningful
            const posRealized = Math.max(realized, 0);
            const posRemaining = Math.max(remaining, 0);
            const negRealized = Math.min(realized, 0);
            const negRemaining = Math.min(remaining, 0);

            const yPosReal = yToPx(posRealized);
            const bhPosReal = Math.abs(yPosReal - y0);
            const yPosRealRect = posRealized > 0 ? yPosReal : y0;

            const yPosRemTop = yPosRealRect - Math.abs(yToPx(posRemaining) - y0);
            const bhPosRem = Math.abs(yToPx(posRemaining) - y0);

            const yNegReal = y0;
            const bhNegReal = Math.abs(yToPx(negRealized) - y0);

            const yNegRem = y0 + bhNegReal;
            const bhNegRem = Math.abs(yToPx(negRemaining) - y0);

            const isNegTotal = total < 0;
            const labelY = isNegTotal ? Math.min(h - padding.bottom - 4, y0 + Math.abs(yToPx(total) - y0) + 12) : Math.max(padding.top + 10, yToPx(total) - 6);

            return (
              <g key={String(poId)}>
                {/* positive stack: realized bottom, remaining on top */}
                {posRealized > 0 ? (
                  <rect x={x} y={yPosRealRect} width={barW} height={bhPosReal} fill="#1976d2" />
                ) : null}
                {posRemaining > 0 ? (
                  <rect x={x} y={yPosRemTop} width={barW} height={bhPosRem} fill="#7fb6e6" />
                ) : null}

                {/* negative stack: realized first then remaining */}
                {negRealized < 0 ? (
                  <rect x={x} y={yNegReal} width={barW} height={bhNegReal} fill="#d32f2f" />
                ) : null}
                {negRemaining < 0 ? (
                  <rect x={x} y={yNegRem} width={barW} height={bhNegRem} fill="#ff9896" />
                ) : null}

                {/* total label */}
                <text x={x + barW / 2} y={labelY} fontSize="11" fill="#111" textAnchor="middle">
                  {formatY(total)}
                </text>

                {/* x labels */}
                {i % labelEvery === 0 ? (
                  <text x={x + barW / 2} y={h - padding.bottom + 16} fontSize="11" fill="#333" textAnchor="middle">
                    {String(poId)}
                  </text>
                ) : null}
              </g>
            );
          })}
        </svg>
      </div>
    </div>
  );
}


// ✅ FIX: this component was referenced but not defined (build crash)
function ProfitPerDayBarChart({ data, height = 220 }) {
  // expected: [{ day, label, total_profit }]
  return (
    <SvgBarChart
      title="Daily profit (Items)"
      data={data}
      xKey="label"
      yKey="total_profit"
      height={height}
      formatX={(v) => String(v ?? "")}
      formatY={(v) => `$${Number(v || 0).toFixed(2)}`}
    />
  );
}

export default function ProfitsDashboard() {
  // default to last 30 days
  const today = new Date();
  const d30 = new Date();
  d30.setDate(today.getDate() - 30);
  const defFrom = d30.toISOString().slice(0, 10);
  const defTo = today.toISOString().slice(0, 10);

  const [activeTab, setActiveTab] = useState("orders"); // "orders" | "items" | "pos"

  const [dateFrom, setDateFrom] = useState(defFrom);
  const [dateTo, setDateTo] = useState(defTo);

  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [items, setItems] = useState([]);
  const [poTrend, setPoTrend] = useState([]);
  const [itemsLoaded, setItemsLoaded] = useState(false);
  const [poTrendLoaded, setPoTrendLoaded] = useState(false);
  const [poTrendOffset, setPoTrendOffset] = useState(0); // in rows (0, 30, 60, ...)

  const PO_TREND_LIMIT = 30;

  const profitPerDay = useMemo(() => {
    if (!Array.isArray(items) || items.length === 0) return [];
    const byDay = new Map();

    for (const r of items) {
      const raw = r?.created_at;
      if (!raw) continue;
      let dayKey = "";
      try {
        const d = new Date(raw);
        // YYYY-MM-DD (local)
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        dayKey = `${y}-${m}-${dd}`;
      } catch {
        // fallback: first 10 chars
        dayKey = String(raw).slice(0, 10);
      }
      const cur = byDay.get(dayKey) || 0;
      byDay.set(dayKey, cur + Number((r?.business_profit ?? r?.item_profit) || 0));
    }

    const rows = Array.from(byDay.entries())
      .map(([day, total_profit]) => {
        const d = new Date(day + "T00:00:00");
        const label = isNaN(d.getTime()) ? day : d.toLocaleDateString(undefined, { day: "2-digit", month: "short" });
        return { day, label, total_profit: Number(total_profit.toFixed(2)) };
      })
      .sort((a, b) => (a.day < b.day ? -1 : 1));

    return rows;
  }, [items]);

  // Items-tab filters
  const [supplierName, setSupplierName] = useState(""); // string filter
  const [poId, setPoId] = useState(""); // numeric id filter (items tab filter)
  const [search, setSearch] = useState(""); // SKU/Title/Card Name

  // --- POs tab state ---
  const [poViewId, setPoViewId] = useState(""); // input for PO Profit view
  const [poData, setPoData] = useState(null); // response from /api/po/:id/profit

  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncingAds, setSyncingAds] = useState(false); // ad-fee sync state
  const [error, setError] = useState("");
  const [info, setInfo] = useState(""); // success/info banner

  const fetchSummary = async () => {
    setError("");
    try {
      const qs = new URLSearchParams({ date_from: dateFrom || "", date_to: dateTo || "" });
      const res = await fetch(`/api/profit/summary?${qs.toString()}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error?.message || "Failed to load summary");
      setSummary(json.data);
    } catch (e) {
      setError(e.message);
    }
  };

  const fetchOrders = async () => {
    setError("");
    try {
      const qs = new URLSearchParams({ date_from: dateFrom || "", date_to: dateTo || "" });
      const res = await fetch(`/api/orders?${qs.toString()}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error?.message || "Failed to load orders");
      setOrders(Array.isArray(json.data) ? json.data : []);
    } catch (e) {
      setError(e.message);
    }
  };

  const fetchItems = async () => {
    setError("");
    try {
      const qs = new URLSearchParams({
        date_from: dateFrom || "",
        date_to: dateTo || "",
        supplier_name: supplierName || "",
        purchase_order_id: poId || "",
        q: search || "",
      });
      const res = await fetch(`/api/items?${qs.toString()}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error?.message || "Failed to load items");
      setItems(Array.isArray(json.data) ? json.data : []);
      setItemsLoaded(true);
    } catch (e) {
      setError(e.message);
    }
  };

  const fetchPOTrend = async (offset = 0) => {
    setError("");
    try {
      const qs = new URLSearchParams({ limit: String(PO_TREND_LIMIT), offset: String(offset || 0) });
      const res = await fetch(`/api/po/profit-trend?${qs.toString()}`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error?.message || "Failed to load PO trend");
      const rows = Array.isArray(json.data) ? json.data : [];
      setPoTrend(rows);
      setPoTrendLoaded(true);
      return rows;
    } catch (e) {
      setError(e.message);
      return null;
    }
  };

  // --- Fetch PO Profit for the POs tab ---
  const fetchPOProfit = async (poId) => {
    setError("");
    try {
      const res = await fetch(`/api/po/${poId}/profit`);
      const json = await res.json();
      if (!json.ok) throw new Error(json.error?.message || "Failed to load PO profit");
      setPoData(json.data);
    } catch (e) {
      setError(e.message);
    }
  };

  const refreshAll = async () => {
    setLoading(true);
    try {
      // Keep initial load lightweight for performance.
      await Promise.all([fetchSummary(), fetchOrders()]);

      // Lazy-load heavier datasets only when needed.
      if (activeTab === "items") {
        await fetchItems();
      }
      if (activeTab === "pos") {
        await fetchPOTrend();
      }
    } finally {
      setLoading(false);
    }
  };

  const syncOrders = async () => {
    setSyncing(true);
    setError("");
    setInfo("");
    try {
      if (!dateFrom) {
        setError("Pick a Date From before syncing.");
        return;
      }
      const res = await fetch(`/api/ebay/orders/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ since: dateFrom }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error?.message || "Sync failed");
      await refreshAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncing(false);
    }
  };

  // Sync ad fees via Finances API proxy endpoint
  const syncAdFees = async () => {
    setSyncingAds(true);
    setError("");
    setInfo("");
    try {
      if (!dateFrom || !dateTo) {
        setError("Pick a Date From/To before syncing ad fees.");
        return;
      }
      const res = await fetch(`/api/ebay/ad-fees/sync`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date_from: dateFrom, date_to: dateTo }),
      });
      const json = await res.json();
      if (!json.ok) throw new Error(json.error?.message || "Ad-fee sync failed");
      const s = json.data || {};
      setInfo(`Ad fees synced: ${s.orders ?? 0} orders, ${s.items ?? 0} items, total ${nfmt(s.ad_fee_total ?? 0)}`);
      await refreshAll();
    } catch (e) {
      setError(e.message);
    } finally {
      setSyncingAds(false);
    }
  };

  useEffect(() => {
    refreshAll();
    /* eslint-disable-next-line */
  }, []);

  // Lazy-load heavy datasets only when the tab is opened.
  useEffect(() => {
    if (activeTab === "items" && !itemsLoaded) {
      setLoading(true);
      fetchItems().finally(() => setLoading(false));
    }
    if (activeTab === "pos" && !poTrendLoaded) {
      setLoading(true);
      setPoTrendOffset(0);
      fetchPOTrend(0).finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="p-4 grid gap-4">
      <Card className="p-4 grid gap-3">
        <div className="grid grid-cols-1 md:grid-cols-8 gap-2 items-end">
          <div>
            <label className="text-sm">Date From</label>
            <input type="date" className="w-full border rounded p-2" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="text-sm">Date To</label>
            <input type="date" className="w-full border rounded p-2" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>

          {/* Items-tab filters */}
          <div>
            <label className="text-sm">Supplier</label>
            <input className="w-full border rounded p-2" placeholder="e.g. Cammez Cards" value={supplierName} onChange={(e) => setSupplierName(e.target.value)} />
          </div>
          <div>
            <label className="text-sm">PO ID</label>
            <input className="w-full border rounded p-2" placeholder="e.g. 345" value={poId} onChange={(e) => setPoId(e.target.value)} />
          </div>
          <div className="md:col-span-2">
            <label className="text-sm">Search</label>
            <input className="w-full border rounded p-2" placeholder="SKU, Title, Card Name-" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>

          <button className="border rounded p-2" onClick={refreshAll} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
          <button className="border rounded p-2" onClick={syncOrders} disabled={syncing}>
            {syncing ? "Syncing..." : "Sync Orders"}
          </button>
          <button className="border rounded p-2" onClick={syncAdFees} disabled={syncingAds}>
            {syncingAds ? "Syncing Ads-" : "Sync Ad Fees"}
          </button>

          {error && (
            <div className="text-sm md:col-span-2" style={{ color: "#d32f2f" }}>
              {error}
            </div>
          )}
          {info && (
            <div className="text-sm md:col-span-2" style={{ color: "#2e7d32" }}>
              {info}
            </div>
          )}
        </div>
      </Card>

      {/* KPI cards */}
      <div className="grid md:grid-cols-5 gap-3">
        <Card className="p-4">
          <div className="text-xs opacity-70">Orders</div>
          <div className="text-2xl font-semibold">{summary?.orders_count ?? "-"}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs opacity-70">Gross</div>
          <div className="text-2xl font-semibold">{nfmt(summary?.gross)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs opacity-70">Fees</div>
          <div className="text-2xl font-semibold">{nfmt(summary?.fees)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs opacity-70">Shipping</div>
          <div className="text-2xl font-semibold">{nfmt(summary?.shipping)}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs opacity-70">Profit</div>
          <div className="text-2xl font-semibold">{nfmt(summary?.profit)}</div>
        </Card>
      </div>

      {activeTab === "pos" && (
        <Card className="p-4">
          <div className="flex items-center justify-end gap-2 mb-2">
            <button
              className="border rounded px-3 py-1"
              disabled={loading || poTrendOffset <= 0}
              onClick={async () => {
                const next = Math.max(0, poTrendOffset - PO_TREND_LIMIT);
                setLoading(true);
                try {
                  const rows = await fetchPOTrend(next);
                  if (rows) setPoTrendOffset(next);
                } finally {
                  setLoading(false);
                }
              }}
            >
              Newer
            </button>
            <button
              className="border rounded px-3 py-1"
              disabled={loading}
              onClick={async () => {
                const next = poTrendOffset + PO_TREND_LIMIT;
                setLoading(true);
                try {
                  const rows = await fetchPOTrend(next);
                  if (Array.isArray(rows) && rows.length > 0) {
                    setPoTrendOffset(next);
                  }
                } finally {
                  setLoading(false);
                }
              }}
            >
              Older
            </button>
          </div>

          <POProfitTrendBarChart
            data={poTrend}
            titleSuffix={poTrendOffset === 0 ? `latest ${PO_TREND_LIMIT}` : `older ${PO_TREND_LIMIT} (offset ${poTrendOffset})`}
          />
        </Card>
      )}

      {activeTab === "items" && (
        <Card className="p-4">
          <ProfitPerDayBarChart data={profitPerDay} />
        </Card>
      )}

      {/* Tabs */}
      <div className="flex gap-2">
        <button className={`px-3 py-2 rounded ${activeTab === "orders" ? "bg-black text-white" : "border"}`} onClick={() => setActiveTab("orders")}>
          Orders
        </button>
        <button className={`px-3 py-2 rounded ${activeTab === "items" ? "bg-black text-white" : "border"}`} onClick={() => setActiveTab("items")}>
          Items
        </button>
        <button className={`px-3 py-2 rounded ${activeTab === "pos" ? "bg-black text-white" : "border"}`} onClick={() => setActiveTab("pos")}>
          POs
        </button>
      </div>

      {/* Orders table */}
      {activeTab === "orders" && (
        <Card className="p-4">
          <div className="text-lg font-semibold mb-2">Orders</div>
          <div className="overflow-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="p-2">Order ID</th>
                  <th className="p-2">Created</th>
                  <th className="p-2">Buyer</th>
                  <th className="p-2">Status</th>
                  <th className="p-2">Gross</th>
                  <th className="p-2">Currency</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(orders) && orders.length ? (
                  orders.map((o) => (
                    <tr key={o.order_id} className="border-t">
                      <td className="p-2">{o.order_id}</td>
                      <td className="p-2">{isoToLocal(o.created_at)}</td>
                      <td className="p-2">{o.buyer_username}</td>
                      <td className="p-2">{o.order_status}</td>
                      <td className="p-2">{nfmt(o.total_value)}</td>
                      <td className="p-2">{o.currency}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td className="p-4 opacity-60" colSpan={6}>
                      No orders yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Items table */}
      {activeTab === "items" && (
        <Card className="p-4">
          <div className="text-lg font-semibold mb-2">Items (with Supplier & PO)</div>
          <div className="overflow-auto">
            <table className="min-w-[1400px] text-sm">
              <thead>
                <tr className="text-left">
                  <th className="p-2">Created</th>
                  <th className="p-2">Order ID</th>
                  <th className="p-2">Item ID</th>
                  <th className="p-2">SKU</th>
                  <th className="p-2">Title</th>
                  <th className="p-2">Supplier</th>
                  <th className="p-2">PO</th>
                  <th className="p-2">Consign</th>
                  <th className="p-2">Comm</th>
                  <th className="p-2">Payout</th>
                  <th className="p-2">Qty</th>
                  <th className="p-2">COGS</th>
                  <th className="p-2">Fees</th>
                  <th className="p-2">Ad Fee</th> {/* NEW */}
                  <th className="p-2">Fees Total</th> {/* NEW */}
                  <th className="p-2">Ship</th>
                  <th className="p-2">Sales</th>
                  <th className="p-2">Profit</th>
                  <th className="p-2">Margin</th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(items) && items.length ? (
                  items.map((r) => {
                    const totalFees = r.item_fees_total != null ? r.item_fees_total : Number(r.item_fee_alloc || 0) + Number(r.item_ad_fee || 0);
                    return (
                      <tr key={`${r.order_id}-${r.item_id}`} className="border-t">
                        <td className="p-2">{isoToLocal(r.created_at)}</td>
                        <td className="p-2">{r.order_id}</td>
                        <td className="p-2">{r.item_id}</td>
                        <td className="p-2">{r.sku}</td>
                        <td className="p-2">{r.title || r.card_name || "-"}</td>
                        <td className="p-2">{r.supplier_name || "-"}</td>
                        <td className="p-2">{r.purchase_order_id ?? "-"}</td>
                        <td className="p-2">{r.is_consignment ? "Y" : ""}</td>
                        <td className="p-2">{nfmt(r.consignment_commission)}</td>
                        <td className="p-2">{nfmt(r.consignor_payout)}</td>
                        <td className="p-2">{nfmt(r.quantity)}</td>
                        <td className="p-2">{nfmt(r.item_cogs)}</td>
                        <td className="p-2">{nfmt(r.item_fee_alloc)}</td>
                        <td className="p-2">{nfmt(r.item_ad_fee)}</td> {/* NEW */}
                        <td className="p-2">{nfmt(totalFees)}</td> {/* NEW */}
                        <td className="p-2">{nfmt(r.item_ship_alloc)}</td>
                        <td className="p-2">{nfmt(r.item_total_price)}</td>
                        <td className="p-2">{nfmt(r.business_profit ?? r.item_profit)}</td>
                        <td className="p-2">{typeof (r.business_margin ?? r.item_margin) === "number" ? `${(((r.business_margin ?? r.item_margin) || 0) * 100).toFixed(1)}%` : "-"}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td className="p-4 opacity-60" colSpan={19}>
                      No items for the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* POs (Purchase Orders) Profit view */}
      {activeTab === "pos" && (
        <Card className="p-4">
          <div className="text-lg font-semibold mb-3">Purchase Order Profit</div>

          {/* Controls */}
          <div className="flex items-end gap-3 mb-4">
            <div>
              <label className="text-sm">PO ID</label>
              <input className="w-40 border rounded p-2" placeholder="e.g. 345" value={poViewId} onChange={(e) => setPoViewId(e.target.value)} />
            </div>
            <button
              className="border rounded p-2"
              disabled={!poViewId || loading}
              onClick={async () => {
                setLoading(true);
                try {
                  await fetchPOProfit(poViewId.trim());
                } finally {
                  setLoading(false);
                }
              }}
            >
              {loading ? "Loading..." : "Load"}
            </button>
            {error && <div className="text-red-600 text-sm">{error}</div>}
          </div>

          {poData ? (
            <>
              {/* Header KPIs */}
              <div className="grid md:grid-cols-5 gap-3 mb-4">
                <Card className="p-4">
                  <div className="text-xs opacity-70">PO</div>
                  <div className="text-2xl font-semibold">{poData.header.purchase_order_id}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs opacity-70">Realized Profit</div>
                  <div className="text-2xl font-semibold">{nfmt(poData.header.realized.profit)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs opacity-70">Projected Remaining</div>
                  <div className="text-2xl font-semibold">{nfmt(poData.header.projected_remaining_profit)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs opacity-70">Total Projected</div>
                  <div className="text-2xl font-semibold">{nfmt(poData.header.total_projected_profit)}</div>
                </Card>
                <Card className="p-4">
                  <div className="text-xs opacity-70">Realized Gross</div>
                  <div className="text-2xl font-semibold">{nfmt(poData.header.realized.gross)}</div>
                </Card>
              </div>

              {/* Realized table */}
              <div className="text-lg font-semibold mb-2">Sold (Realized)</div>
              <div className="overflow-auto mb-6">
                <table className="min-w-[1100px] text-sm">
                  <thead>
                    <tr className="text-left">
                      <th className="p-2">SKU</th>
                      <th className="p-2">Card</th>
                      <th className="p-2">Qty Sold</th>
                      <th className="p-2">COGS Used</th>
                      <th className="p-2">Sales</th>
                      <th className="p-2">Fees</th>
                      <th className="p-2">Ship</th>
                      <th className="p-2">Profit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(poData.realized_lines) && poData.realized_lines.length ? (
                      poData.realized_lines.map((r) => (
                        <tr key={`${r.listing_id}-${r.sku || ""}`} className="border-t">
                          <td className="p-2">{r.sku}</td>
                          <td className="p-2">{r.card_name}</td>
                          <td className="p-2">{nfmt(r.qty_sold)}</td>
                          <td className="p-2">{nfmt(r.cogs_used)}</td>
                          <td className="p-2">{nfmt(r.sales_gross)}</td>
                          <td className="p-2">{nfmt(r.fees)}</td>
                          <td className="p-2">{nfmt(r.ship_out)}</td>
                          <td className="p-2">{nfmt(r.realized_profit)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="p-4 opacity-60" colSpan={8}>
                          No realized sales yet.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Projected table */}
              <div className="text-lg font-semibold mb-2">Remaining (Projected)</div>
              <div className="overflow-auto">
                <table className="min-w-[1100px] text-sm">
                  <thead>
                    <tr className="text-left">
                      <th className="p-2">SKU</th>
                      <th className="p-2">Card</th>
                      <th className="p-2">Qty Remaining</th>
                      <th className="p-2">Unit COGS</th>
                      <th className="p-2">Predicted Price</th>
                      <th className="p-2">Projected Revenue</th>
                      <th className="p-2">Projected Profit (Predicted)</th>
                      <th className="p-2">Current Listed Price</th>
                      <th className="p-2">Projected Profit (Actual)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(poData.projected_lines) && poData.projected_lines.length ? (
                      poData.projected_lines.map((p) => (
                        <tr key={`${p.listing_id}-${p.sku || ""}`} className="border-t">
                          <td className="p-2">{p.sku}</td>
                          <td className="p-2">{p.card_name}</td>
                          <td className="p-2">{nfmt(p.qty_remaining)}</td>
                          <td className="p-2">{nfmt(p.unit_cogs)}</td>
                          <td className="p-2">{nfmt(p.predicted_sale_price)}</td>
                          <td className="p-2">{nfmt(p.projected_revenue)}</td>
                          <td className="p-2">{nfmt(p.projected_profit)}</td>
                          <td className="p-2">{nfmt(p.current_listed_price)}</td>
                          <td className="p-2">{nfmt(p.projected_profit_actual)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td className="p-4 opacity-60" colSpan={9}>
                          No projected items (everything sold or no predictions).
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <div className="opacity-60">Enter a PO ID and click Load.</div>
          )}
        </Card>
      )}
    </div>
  );
}
